import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  Toolbar,
  TablePagination,
  Switch,
} from '@mui/material';
import {
  Close,
  Add,
  Edit,
  Delete,
  Search,
  AccountBalanceWallet,
  CreditCard,
  Payment,
  Receipt,
  QrCode,
  VpnKey,
  AccountBalance,
  Store,
  AccountBalanceOutlined,
  Lock,
  LockOpen,
} from '@mui/icons-material';
import formaPgtoService, { FormaPgto } from '../../services/formaPgtoService';
import FormaPgtoForm from '../forms/FormaPgtoForm';

interface FormaPgtoModalProps {
  open: boolean;
  onClose: () => void;
}

// Mapeamento de ícones por nome
const iconMap: { [key: string]: React.ReactElement } = {
  'AccountBalanceWallet': <AccountBalanceWallet />,
  'CreditCard': <CreditCard />,
  'Payment': <Payment />,
  'Receipt': <Receipt />,
  'QrCode': <QrCode />,
  'VpnKey': <VpnKey />,
  'AccountBalance': <AccountBalance />,
  'AccountBalanceOutlined': <AccountBalanceOutlined />,
  'Store': <Store />,
};

const FormaPgtoModal: React.FC<FormaPgtoModalProps> = ({
  open,
  onClose
}) => {
  const [formasPgto, setFormasPgto] = useState<FormaPgto[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);
  
  // Estados do formulário
  const [formOpen, setFormOpen] = useState(false);
  const [editingFormaPgto, setEditingFormaPgto] = useState<FormaPgto | null>(null);

  // Carregar formas de pagamento
  const loadFormasPgto = async () => {
    try {
      setLoading(true);
      const response = await formaPgtoService.getAllFormas({
        page: page + 1,
        limit: rowsPerPage,
        search: searchTerm || undefined,
      });
      
      setFormasPgto(response.formas);
      setTotal(response.pagination.total);
    } catch (error) {
      console.error('Erro ao carregar formas de pagamento:', error);
      setMessage({ type: 'error', text: 'Erro ao carregar formas de pagamento' });
    } finally {
      setLoading(false);
    }
  };

  // Carregar dados quando modal abrir
  useEffect(() => {
    if (open) {
      loadFormasPgto();
    }
  }, [open, page, rowsPerPage, searchTerm]);

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setPage(0); // Reset para primeira página
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleCreate = () => {
    setEditingFormaPgto(null);
    setFormOpen(true);
  };

  const handleEdit = (formaPgto: FormaPgto) => {
    setEditingFormaPgto(formaPgto);
    setFormOpen(true);
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Tem certeza que deseja excluir esta forma de pagamento?')) {
      try {
        await formaPgtoService.deleteForma(id);
        setMessage({ type: 'success', text: 'Forma de pagamento excluída com sucesso!' });
        loadFormasPgto();
      } catch (error) {
        console.error('Erro ao excluir forma de pagamento:', error);
        setMessage({ type: 'error', text: 'Erro ao excluir forma de pagamento' });
      }
    }
  };

  const handleToggleAtivo = async (id: number) => {
    try {
      await formaPgtoService.toggleAtivo(id);
      setMessage({ type: 'success', text: 'Status alterado com sucesso!' });
      loadFormasPgto();
    } catch (error) {
      console.error('Erro ao alterar status:', error);
      setMessage({ type: 'error', text: 'Erro ao alterar status' });
    }
  };

  const handleFormSuccess = (formaPgto: FormaPgto) => {
    setMessage({ type: 'success', text: 'Forma de pagamento salva com sucesso!' });
    setFormOpen(false);
    loadFormasPgto();
  };

  const handleFormError = (error: string) => {
    setMessage({ type: 'error', text: error });
  };

  const handleClose = () => {
    if (!loading) {
      setFormasPgto([]);
      setSearchTerm('');
      setPage(0);
      setMessage(null);
      onClose();
    }
  };

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="h6">
              Gerenciar Formas de Pagamento
            </Typography>
            <Box display="flex" gap={1}>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={handleCreate}
                size="small"
              >
                Cadastrar
              </Button>
              <IconButton onClick={handleClose}>
                <Close />
              </IconButton>
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent>
          {message && (
            <Alert severity={message.type} sx={{ mb: 2 }}>
              {message.text}
            </Alert>
          )}

          <Toolbar sx={{ pl: 0, pr: 0 }}>
            <TextField
              fullWidth
              placeholder="Buscar formas de pagamento..."
              value={searchTerm}
              onChange={handleSearch}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
              size="small"
            />
          </Toolbar>

          <TableContainer component={Paper} sx={{ mt: 2 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Ícone</TableCell>
                  <TableCell>Nome</TableCell>
                  <TableCell>Descrição</TableCell>
                  <TableCell>Ordem</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="center">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : formasPgto.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">
                        Nenhuma forma de pagamento encontrada
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  formasPgto.map((forma) => (
                    <TableRow key={forma.id}>
                      <TableCell>
                        {iconMap[forma.icone || 'Payment'] || <Payment />}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {forma.nome}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {forma.descricao || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {forma.ordem}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={forma.ativo ? 'Ativo' : 'Inativo'}
                          color={forma.ativo ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Box display="flex" gap={1} justifyContent="center">
                          {forma.is_default === false && (
                            <IconButton
                              size="small"
                              onClick={() => handleEdit(forma)}
                              color="primary"
                            >
                              <Edit />
                            </IconButton>
                          )}
                          <Switch
                            checked={forma.ativo ? true : false}
                            onChange={() => handleToggleAtivo(forma.id)}
                            color="primary"
                          />                            
                          {forma.is_default === false && (
                            <IconButton
                              size="small"
                              onClick={() => handleDelete(forma.id)}
                              color="error"
                            >
                              <Delete />
                            </IconButton>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={total}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="Linhas por página:"
            labelDisplayedRows={({ from, to, count }) => 
              `${from}-${to} de ${count !== -1 ? count : `mais de ${to}`}`
            }
          />
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleClose}>
            Fechar
          </Button>
        </DialogActions>
      </Dialog>

      <FormaPgtoForm
        open={formOpen}
        onClose={() => setFormOpen(false)}
        formaPgto={editingFormaPgto}
        onSuccess={handleFormSuccess}
        onError={handleFormError}
      />
    </>
  );
};

export default FormaPgtoModal;
